import { MapPin } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { LOCATION } from "@/lib/site";

export default function LocationMap() {
  return (
    <section id="location" className="bg-cream py-24 sm:py-32" data-testid="location-section">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-[1fr_1.6fr] gap-12 items-center">
        <div>
          <Reveal>
            <p className="eyebrow text-arc mb-6">Where We Are</p>
          </Reveal>
          <MaskedLines
            className="font-serif font-medium tracking-tight leading-[1.05] text-charcoal text-4xl sm:text-5xl"
            lines={[<>Tarneit, VIC 3029.</>, <><span className="italic text-gradient">Victoria, Australia.</span></>]}
          />
          <Reveal delay={0.2}>
            <p className="mt-6 text-mist leading-relaxed max-w-sm">
              Based in Tarneit and working with clients across Tarneit, Melbourne's west and
              surrounds.
            </p>
            <div className="mt-8 flex items-start gap-3">
              <MapPin size={20} className="text-arc mt-0.5" />
              <div>
                <p className="font-serif text-2xl text-charcoal">ArcView Homes</p>
                <p className="text-mist mt-1">{LOCATION.line1} · {LOCATION.line2}</p>
              </div>
            </div>
            <a
              href={LOCATION.directions}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="location-get-directions-button"
              className="mt-8 inline-flex items-center gap-3 font-semibold text-charcoal border-b-2 border-arc pb-1 hover:text-arc transition-colors duration-300"
            >
              Get Directions
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="border border-line p-2 bg-white" data-testid="location-map-frame">
            <div className="bg-sand relative overflow-hidden">
              <svg viewBox="0 0 640 460" className="w-full h-auto block" role="img" aria-label="Stylised map of Tarneit VIC 3029 and Melbourne's west">
                {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <line key={`v${i}`} x1={i * 80 + 40} y1="0" x2={i * 80 + 40} y2="460" stroke="#E7E1D8" strokeWidth="1" />
                ))}
                {[0, 1, 2, 3, 4].map((i) => (
                  <line key={`h${i}`} x1="0" y1={i * 92 + 46} x2="640" y2={i * 92 + 46} stroke="#E7E1D8" strokeWidth="1" />
                ))}
                <path d="M-10 330 C 140 300, 320 340, 480 310 S 640 330, 660 305" stroke="#1D6FB5" strokeWidth="5" fill="none" opacity="0.85" />
                <text x="26" y="364" fontFamily="Space Mono, monospace" fontSize="12" letterSpacing="2" fill="#1D6FB5">M1 FREEWAY</text>
                <path d="M250 225 C 330 260, 420 330, 520 420" stroke="#8A939B" strokeWidth="2.5" strokeDasharray="8 6" fill="none" />
                <text x="330" y="300" fontFamily="Space Mono, monospace" fontSize="10" letterSpacing="2" fill="#8A939B">RAIL</text>
                <path d="M560 200 H 620 M612 194 L 622 200 L 612 206" stroke="#9AA0A6" strokeWidth="2" fill="none" />
                <text x="470" y="185" fontFamily="Space Mono, monospace" fontSize="10" letterSpacing="2" fill="#9AA0A6">TO MELBOURNE CBD</text>
                {[
                  { x: 340, y: 110, name: "TRUGANINA" },
                  { x: 470, y: 235, name: "WILLIAMS LANDING" },
                  { x: 545, y: 330, name: "POINT COOK" },
                  { x: 420, y: 375, name: "HOPPERS CROSSING" },
                  { x: 510, y: 425, name: "WERRIBEE" },
                ].map((s) => (
                  <g key={s.name}>
                    <circle cx={s.x} cy={s.y} r="4" fill="#8A939B" />
                    <text x={s.x + 10} y={s.y + 4} fontFamily="Space Mono, monospace" fontSize="10" letterSpacing="1.5" fill="#6B7178">{s.name}</text>
                  </g>
                ))}
                <circle cx="250" cy="225" r="18" fill="none" stroke="#E97E2E" strokeWidth="1.5" opacity="0.5" />
                <circle cx="250" cy="225" r="8" fill="#E97E2E" />
                <text x="250" y="192" textAnchor="middle" fontFamily="Space Mono, monospace" fontSize="14" fontWeight="bold" letterSpacing="3" fill="#1A1D20">TARNEIT</text>
                <text x="250" y="262" textAnchor="middle" fontFamily="Space Mono, monospace" fontSize="10" letterSpacing="2" fill="#5A6065">VIC 3029</text>
                <g transform="translate(592,58)">
                  <path d="M0 14 L0 -14 M0 -14 L-6 -4 M0 -14 L6 -4" stroke="#5A6065" strokeWidth="2" fill="none" strokeLinecap="round" />
                  <text x="0" y="32" textAnchor="middle" fontFamily="Space Mono, monospace" fontSize="11" fill="#5A6065">N</text>
                </g>
                <g transform="translate(26,60)">
                  <rect width="150" height="34" fill="none" stroke="#D8D2C8" />
                  <text x="10" y="14" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2" fill="#8A939B">ARCVIEW HOMES</text>
                  <text x="10" y="27" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2" fill="#E97E2E">SERVICE AREA</text>
                </g>
              </svg>
              <div className="absolute bottom-3 left-3 bg-ink/85 text-cream font-mono text-[9px] tracking-[0.25em] px-3 py-2">
                SERVICING MELBOURNE'S WEST
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
